import React, { useRef } from "react";
import {
  Container,
  Box,
  Typography,
  Stack,
  Button,
  GlobalStyles,
  Fade,
  Paper,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import localization from "../utils/localization";
import logo256 from "../assets/logo_256.png";

export function Refund() {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const sections = [
    {
      title: localization.strings.refundSection1Title,
      content: localization.strings.refundSection1Content,
      icon: "icon-check",
      color: "#4CAF50",
    },
    {
      title: localization.strings.refundSection2Title,
      content: localization.strings.refundSection2Content,
      icon: "icon-envelope",
      color: "#1E88E5",
    },
    {
      title: localization.strings.refundSection3Title,
      content: localization.strings.refundSection3Content,
      icon: "icon-close",
      color: "#EF5350",
    },
  ];

  return (
    <Box
      ref={containerRef}
      sx={{
        backgroundColor: "#F8FAFC",
        width: "100%",
        height: "100%",
        overflowY: "auto",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <GlobalStyles
        styles={{
          body: { margin: 0, padding: 0 },
        }}
      />

      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{ cursor: "pointer" }}
              onClick={() => navigate("/")}
            >
              <Box
                sx={{
                  p: "2px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)",
                  boxShadow: "0 8px 16px rgba(30, 136, 229, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={logo256}
                  alt="EasyPass Logo"
                  sx={{
                    display: "block",
                    height: "2rem",
                    width: "2rem",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    p: "1px",
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: "#1E293B",
                  ml: 2,
                  letterSpacing: "-0.03em",
                  fontSize: "1.2rem",
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                EasyPass
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small">
                <Select
                  value={localization.language.lang || "en"}
                  onChange={(event) => {
                    window.location.href = "?lang=" + event.target.value;
                  }}
                  sx={{
                    color: "#1E293B",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    backgroundColor: "rgba(0,0,0,0.04)",
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    height: "2.4rem",
                  }}
                >
                  <MenuItem value="en">EN</MenuItem>
                  <MenuItem value="zh">ZH</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="text"
                onClick={() => navigate("/")}
                sx={{
                  color: "#64748B",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { color: "#1E88E5" },
                }}
              >
                {localization.strings.goBack}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1E293B 0%, #1E88E5 100%)",
          py: { xs: 6, md: 10 },
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-20%",
            right: "-5%",
            width: "35%",
            height: "140%",
            background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 72,
              height: 72,
              borderRadius: "20px",
              backgroundColor: "rgba(255,255,255,0.15)",
              mb: 3,
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <i className="iconfont icon-lock" style={{ fontSize: "2rem", color: "#fff" }} />
          </Box>
          <Typography
            variant="h3"
            sx={{
              color: "#fff",
              fontWeight: 800,
              mb: 2,
              fontFamily: "'Poppins', sans-serif",
              fontSize: { xs: "2rem", md: "2.8rem" },
            }}
          >
            {localization.strings.refundTitle}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "rgba(255,255,255,0.75)",
              fontSize: "1.1rem",
              maxWidth: "560px",
              mx: "auto",
            }}
          >
            {localization.strings.refundSubTitle}
          </Typography>
        </Container>
      </Box>

      {/* 14-day badge */}
      <Container maxWidth="md">
        <Box
          sx={{
            mt: -4,
            mb: 6,
            p: 3,
            paddingTop: "3rem",
            borderRadius: "20px",
            background: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
            boxShadow: "0 16px 40px rgba(76, 175, 80, 0.3)",
            display: "flex",
            alignItems: "center",
            gap: 3,
            color: "#fff",
          }}
        >
          <Box
            sx={{
              minWidth: 56,
              height: 56,
              borderRadius: "16px",
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="iconfont icon-check" style={{ fontSize: "1.6rem", color: "#fff" }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: "'Poppins', sans-serif" }}>
              14-Day Money-Back Guarantee
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
              Not satisfied? Get a full refund within 14 days — no questions asked.
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Content Sections */}
      <Container maxWidth="md" sx={{ pb: 10 }}>
        <Fade in={true} timeout={800}>
          <Stack spacing={4}>
            {sections.map((section, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: "24px",
                  border: "1px solid rgba(0,0,0,0.06)",
                  backgroundColor: "#fff",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 12px 32px rgba(0,0,0,0.06)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={3}>
                  <Box
                    sx={{
                      minWidth: 48,
                      height: 48,
                      borderRadius: "14px",
                      backgroundColor: `${section.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 0.5,
                    }}
                  >
                    <i
                      className={`iconfont ${section.icon}`}
                      style={{ fontSize: "1.3rem", color: section.color }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        color: "#1E293B",
                        fontWeight: 700,
                        mb: 1.5,
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: { xs: "1.1rem", md: "1.3rem" },
                      }}
                    >
                      {section.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#475569",
                        lineHeight: 1.9,
                        fontSize: "1.05rem",
                      }}
                    >
                      {section.content}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Fade>

        {/* Contact CTA */}
        <Box
          sx={{
            mt: 8,
            p: { xs: 4, md: 6 },
            borderRadius: "24px",
            background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
            border: "1px solid rgba(30, 136, 229, 0.15)",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#1E293B", mb: 1, fontFamily: "'Poppins', sans-serif" }}
          >
            Need Help?
          </Typography>
          <Typography variant="body1" sx={{ color: "#64748B", mb: 3 }}>
            Our support team is ready to assist you with any refund requests.
          </Typography>
          <Button
            variant="contained"
            disableElevation
            href="mailto:easypass.inc@gmail.com"
            startIcon={<i className="iconfont icon-envelope" style={{ fontSize: "1.1rem" }} />}
            sx={{
              backgroundColor: "#1E88E5",
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 700,
              px: 4,
              py: 1.4,
              fontSize: "1rem",
              boxShadow: "0 8px 20px rgba(30, 136, 229, 0.3)",
              "&:hover": {
                backgroundColor: "#1565C0",
                boxShadow: "0 12px 28px rgba(30, 136, 229, 0.4)",
              },
            }}
          >
            easypass.inc@gmail.com
          </Button>
        </Box>

        <Box sx={{ mt: 6, textAlign: "center", borderTop: "1px solid rgba(0,0,0,0.06)", pt: 4 }}>
          <Typography variant="body2" sx={{ color: "#94A3B8" }}>
            Last updated: June 6, 2026
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Refund;
